// URL builders for the Laravel backend's `/register` and `/sso/<provider>/redirect`
// endpoints. Wrap the inline computed properties that previously lived in
// `app/pages/login.vue` and `app/components/marketing/OrganizeSection.vue`,
// adding optional `signup_source_type` + `signup_source_id` params used by the
// Phase 6b signup-source tracking flow.
//
// Pure functions, no Vue/Nuxt imports — auto-imported by Nuxt from `app/utils/`.

import { safePath } from '~/utils/safe-redirect';

export type SignupSource = { type: string; id: string };

// Events-frontend default: bare `/` (the public browse page). Hunt-frontend
// uses `/dashboard` — note `safePath()`'s built-in default is also `/dashboard`,
// so we always pass an explicit fallback here to override that.
const DEFAULT_NEXT = '/';

function formatSourceParams(source?: SignupSource | null): string {
  if (!source) return '';
  return (
    `&signup_source_type=${encodeURIComponent(source.type)}` +
    `&signup_source_id=${encodeURIComponent(source.id)}`
  );
}

interface BuildSignupUrlOpts {
  plannerOrigin: string;
  frontendOrigin: string;
  redirectTo?: unknown;
  source?: SignupSource | null;
}

/**
 * Build the full `/register` URL on the Laravel backend.
 *
 * The `return_url` lands the user on this frontend's `/login` page after
 * sign-up; if a safe `redirectTo` is supplied, it round-trips as a
 * `redirect_to` query param on that login URL so the user is forwarded on
 * to their original destination after first sign-in.
 *
 * Returns `''` when `plannerOrigin` is blank — guards against rendering a
 * URL with no host (e.g. before runtime config is hydrated in tests).
 */
export function buildSignupUrl(opts: BuildSignupUrlOpts): string {
  const { plannerOrigin, frontendOrigin, redirectTo, source } = opts;
  if (!plannerOrigin) return '';

  // Only forward the redirect_to query param to /login when it is a safe
  // same-origin path AND it differs from the default — matches the existing
  // login.vue logic prior to this refactor.
  const safe = safePath(redirectTo, DEFAULT_NEXT);
  const loginPath =
    safe !== DEFAULT_NEXT
      ? `/login?redirect_to=${encodeURIComponent(safe)}`
      : '/login';

  const returnUrl = encodeURIComponent(frontendOrigin + loginPath);
  return (
    `${plannerOrigin}/register?return_url=${returnUrl}` +
    formatSourceParams(source)
  );
}

interface BuildSsoUrlOpts {
  provider: 'google' | 'facebook';
  ssoOrigin: string;
  frontendOrigin: string;
  redirectTo?: unknown;
  source?: SignupSource | null;
}

/**
 * Build the full `/sso/<provider>/redirect` URL on the Laravel backend.
 *
 * The `return_url` points back at this frontend's `/auth/callback?next=<path>`
 * where `<path>` is the validated `redirectTo` (or `/` when missing/unsafe).
 *
 * Returns `''` when `ssoOrigin` is blank.
 */
export function buildSsoUrl(opts: BuildSsoUrlOpts): string {
  const { provider, ssoOrigin, frontendOrigin, redirectTo, source } = opts;
  if (!ssoOrigin) return '';

  const next = safePath(redirectTo, DEFAULT_NEXT);
  const callbackUrl = `${frontendOrigin}/auth/callback?next=${encodeURIComponent(next)}`;

  return (
    `${ssoOrigin}/sso/${provider}/redirect` +
    `?return_url=${encodeURIComponent(callbackUrl)}` +
    formatSourceParams(source)
  );
}
